import { type AcademyDetails, normalisePhone } from '@musicville/shared';
import { useAcademyDetails } from './queries';

export const ACADEMY = {
  name: 'MusicVille Academy',
  email: 'musicvilleacademy@gmail.com',
  phoneDisplay: '+994 55 588 85 33',
  phoneE164: '+994555888533',
  address: 'Nizami küçəsi 30D, Bakı',
  timezone: 'Asia/Baku',
  trialMinutes: 30,
  get whatsappUrl(): string {
    return `https://wa.me/${this.phoneE164.replace('+', '')}`;
  },
  get mapUrl(): string {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.address)}`;
  },
  openMinute: 600,
  closeMinute: 1260,
  closedWeekdays: [],
} as const;

export interface AcademyContact {
  name: string;
  email: string;
  phoneDisplay: string;
  phoneE164: string;
  address: string;
  whatsappUrl: string;
  mapUrl: string;
  openMinute: number;
  closeMinute: number;
  closedWeekdays: readonly number[];
  legalDraft: boolean;
}

export function academyContact(details: AcademyDetails | undefined): AcademyContact {
  const phoneDisplay = details?.phone ?? ACADEMY.phoneDisplay;
  const phoneE164 = details?.phone ? normalisePhone(details.phone) : ACADEMY.phoneE164;
  const address = details?.address ?? ACADEMY.address;
  return {
    name: details?.name ?? ACADEMY.name,
    email: details?.email ?? ACADEMY.email,
    phoneDisplay,
    phoneE164,
    address,
    whatsappUrl: `https://wa.me/${phoneE164.replace('+', '')}`,
    mapUrl: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`,
    openMinute: details?.openMinute ?? ACADEMY.openMinute,
    closeMinute: details?.closeMinute ?? ACADEMY.closeMinute,
    closedWeekdays: details?.closedWeekdays ?? ACADEMY.closedWeekdays,
    legalDraft: details?.legalDraft ?? true,
  };
}

type Translate = (key: string, vars?: Record<string, string | number>) => string;

const MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0];

export interface OpeningHours {
  open: string | null;
  closed: string | null;
}

export function openingHours(
  t: Translate,
  academy: Pick<AcademyContact, 'openMinute' | 'closeMinute' | 'closedWeekdays'>,
): OpeningHours {
  const days = (wanted: boolean): string | null => {
    const runs: number[][] = [];
    for (const day of MONDAY_FIRST) {
      if (academy.closedWeekdays.includes(day) !== wanted) continue;
      const run = runs.at(-1);
      const previous = run?.at(-1);
      if (
        run &&
        previous !== undefined &&
        MONDAY_FIRST.indexOf(previous) === MONDAY_FIRST.indexOf(day) - 1
      ) {
        run.push(day);
      } else {
        runs.push([day]);
      }
    }
    if (runs.length === 0) return null;
    return runs
      .map((run) => {
        const first = t(`common.weekdayShort.${run[0]}`);
        const last = t(`common.weekdayShort.${run.at(-1)}`);
        return run.length === 1 ? first : `${first}–${last}`;
      })
      .join(', ');
  };

  const open = days(false);
  const closed = days(true);
  return {
    open:
      open &&
      t('contact.hoursShort', {
        days: open,
        hours: `${clock(academy.openMinute)} – ${clock(academy.closeMinute)}`,
      }),
    closed: closed && t('contact.closedOn', { days: closed }),
  };
}

function clock(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

export function useAcademy(): AcademyContact {
  const { data } = useAcademyDetails();
  return academyContact(data);
}
